import { useEffect } from 'react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button.tsx';
import { useConfirmEmailQuery } from '@/features/auth/hooks/mutations/confirm-email.ts';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { Input } from '@/components/ui/input.tsx';
import { useTranslation } from 'react-i18next';
import { getConfirmEmailSchema } from './register/steps.tsx';
import { useAuthStore } from '@/services/store/use-auth-store.tsx';
import { StatusAlert } from '@/features/auth/components/status-alert.tsx';
import { PrimeLogo } from '@/assets/prime-logo.tsx';

export const ConfirmEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const { isAuthenticated } = useAuthStore();

  const confirmEmailSchema = getConfirmEmailSchema({
    codeRequired: t('auth.errors.codeRequired'),
    codeLength: t('auth.errors.codeLength'),
    codeNumber: t('auth.errors.codeNumber'),
    codePositive: t('auth.errors.codePositive'),
    emailRequired: t('auth.errors.emailRequired'),
    emailValid: t('auth.errors.emailValid'),
  });

  const { email, code } = {
    email: searchParams.get('email') || '',
    code: searchParams.get('code') || '',
  };

  const form = useForm({
    resolver: zodResolver(confirmEmailSchema),
    defaultValues: {
      email,
      code,
    },
  });

  const {
    mutate: confirmEmailMutation,
    isPending,
    error,
  } = useConfirmEmailQuery({
    onSuccess: () => {
      setTimeout(() => {
        navigate('/');
      }, 700);
    },
  });

  async function onSubmit(values: z.infer<typeof confirmEmailSchema>) {
    confirmEmailMutation({
      code: values.code,
      email: values.email,
    });
  }

  useEffect(() => {
    if (email && code) {
      confirmEmailMutation({
        code,
        email,
      });
    }
  }, [email, code]);

  if (isAuthenticated || !code || !email) {
    return (
      <div className="min-h-[390px]">
        <Navigate to="/" replace={true} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col items-center justify-center gap-2">
        <PrimeLogo width="200" height="58" />
      </div>
      <div className="mt-8 flex max-w-[400px] flex-col gap-4">
        {!!error?.message && <StatusAlert type="error" message={error.message} />}
        <h2 className="text-xl font-bold">{t('auth.confirmEmail.title')}</h2>
        <span className="block">
          {t('auth.confirmEmail.description')}{' '}
          <span className="font-bold">
            {form.getValues('email') ? `${form.getValues('email')}.` : ''}
          </span>
        </span>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="code" className="text-[16px]">
                      {t('auth.confirmEmail.code')}
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          {...field}
                          id="code"
                          placeholder=""
                          minLength={3}
                          className="pl-8 text-[16px]"
                        />
                        <span className="text-muted-foreground absolute top-[17px] left-3 h-4 text-sm leading-1">
                          PB-
                        </span>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <Button
              disabled={isPending}
              type="submit"
              className="flex w-full items-center gap-2"
              size="lg"
            >
              {isPending && <LoadingSpinner size={24} />}
              <span className="text-[16px] select-none">
                {t('auth.confirmEmail.confirmButton')}
              </span>
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
};
